/**
 * Workflow Orchestration Mega Menu
 *
 * Sidebar mega-menu showing all 25 primitives organized in 5 phase columns.
 * Designed to be used as a sub-menu for the "Workflow Orchestration" nav group.
 *
 * Features:
 * - 5-column layout (DESIGN, CONFIGURE, EXECUTE, MONITOR, OPTIMIZE)
 * - 5 primitives per column
 * - Phase-colored headers
 * - Compact display for sidebar width
 */

import {
  Box,
  Group,
  Stack,
  Text,
  UnstyledButton,
  Tooltip,
  SimpleGrid,
} from '@mantine/core';
import { useNavigate, useLocation } from 'react-router-dom';

import {
  getPrimitivesByPhase,
  LIFECYCLE_PHASES,
  PHASE_CONFIG,
  type PrimitiveMetadata,
  type LifecyclePhase,
} from '@/config/primitive-metadata';

interface WorkflowOrchestrationMegaMenuProps {
  onNavigate?: () => void;
}

export function WorkflowOrchestrationMegaMenu({ onNavigate }: WorkflowOrchestrationMegaMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigate = (path: string) => {
    navigate(path);
    onNavigate?.();
  };

  return (
    <Box p="xs">
      <SimpleGrid cols={5} spacing="xs">
        {LIFECYCLE_PHASES.map((phase) => (
          <PhaseColumn
            key={phase}
            phase={phase}
            primitives={getPrimitivesByPhase(phase)}
            currentPath={location.pathname}
            onNavigate={handleNavigate}
          />
        ))}
      </SimpleGrid>
    </Box>
  );
}

interface PhaseColumnProps {
  phase: LifecyclePhase;
  primitives: PrimitiveMetadata[];
  currentPath: string;
  onNavigate: (path: string) => void;
}

function PhaseColumn({ phase, primitives, currentPath, onNavigate }: PhaseColumnProps) {
  const config = PHASE_CONFIG[phase];

  return (
    <Stack gap={4}>
      <Text
        size="10px"
        fw={700}
        tt="uppercase"
        c={config.color}
        style={{ letterSpacing: '0.5px' }}
      >
        {config.label}
      </Text>
      {primitives.map((primitive) => {
        const Icon = primitive.icon;
        const isActive = currentPath.startsWith(primitive.path);

        return (
          <Tooltip
            key={primitive.id}
            label={primitive.description}
            position="right"
            withArrow
          >
            <UnstyledButton
              onClick={() => onNavigate(primitive.path)}
              style={{
                padding: '4px 6px',
                borderRadius: 'var(--mantine-radius-sm)',
                background: isActive ? `var(--mantine-color-${config.color}-1)` : 'transparent',
                transition: 'background 0.1s',
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = 'var(--mantine-color-gray-1)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              <Group gap={4} wrap="nowrap">
                <Icon
                  size={12}
                  color={`var(--mantine-color-${config.color}-6)`}
                />
                <Text
                  size="11px"
                  fw={isActive ? 600 : 400}
                  c={isActive ? config.color : undefined}
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {primitive.name}
                </Text>
              </Group>
            </UnstyledButton>
          </Tooltip>
        );
      })}
    </Stack>
  );
}

export default WorkflowOrchestrationMegaMenu;
